import { BrowserRouter, Route, Switch, Redirect, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import * as routes from "./shared/utils/const";
import { QueryClient, QueryClientProvider } from "react-query";
import AppNotification from "./shared/components/notifications/AppNotification";
import { get } from "./shared/utils/LocaleStorage";
import { useAppStore } from "./shared/appStore/useAppStore";
import { setAuthorizationHeaderToAllRequests } from "./api/Api";

// Lazy pages
const LoginPage = lazy(() => import("./routes/landing/login/LoginPage"));
const ExportPage = lazy(() => import("./routes/landing/export/ExportPage"));
const SignUpPage = lazy(() => import("./routes/landing/signup/SignUpPage"));
const FirstLoginPage = lazy(() => import("./routes/landing/firstLogin/FirstLoginPage"));
const HomePage = lazy(() => import("./routes/landing/home/HomePage"));
const ResetPage = lazy(() => import("./routes/landing/reset/ResetPage"));
const SuccessPage = lazy(() => import("./routes/landing/success/SuccessPage"));
const DoctorArea = lazy(() => import("./routes/doctor/DoctorArea"));
const HospitalArea = lazy(() => import("./routes/hospital/HospitalArea"));
const PharmacyArea = lazy(() => import("./routes/pharmacy/PharmacyArea"));
const LaboArea = lazy(() => import("./routes/labo/LaboArea"));
const NotFound = lazy(() => import("./routes/shared/errors/NotFoundPage"));
const TermsPage = lazy(() => import("./routes/landing/terms/Terms"));
const PrivacyPolicyPage = lazy(() => import("./routes/landing/terms/Privacy"));
const AboutPage = lazy(() => import("./routes/landing/home/AboutPage"));
const StaffPage = lazy(() => import("./routes/landing/home/StaffPage"));

const queryClient = new QueryClient();

// Constants
const WARNING_TIME = 14 * 60 * 1000; // 14 minutes
const LOGOUT_TIME = 15 * 60 * 1000; // 15 minutes
const LAST_ACTIVITY_KEY = "lastActivityTime";
const WARNING_SHOWN_KEY = "warningShown";

// MAIN APP
export default function App() {
  const addUser = useAppStore((state) => state.addUser);
  const addToken = useAppStore((state) => state.addToken);
  const saveRole = useAppStore((state) => state.setRole);

  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);

  // Timers
  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const countdownTimer = useRef(null);

  const EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

  // Clear timers
  const clearAllTimers = () => {
    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownTimer.current);
  };

  // Remove listeners
  const removeActivityListeners = () => {
    EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
  };

  // Add listeners
  const addActivityListeners = () => {
    EVENTS.forEach((event) => window.addEventListener(event, resetTimer));
  };

  // Update last activity timestamp
  const updateLastActivityTime = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  };

  // Check session on app load
  const checkSessionOnLoad = () => {
    const lastActivityTimeStr = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivityTimeStr) {
      updateLastActivityTime(); // Initialize if not exists
      return false;
    }

    const lastActivityTime = parseInt(lastActivityTimeStr);
    const currentTime = Date.now();
    const timeSinceLastActivity = currentTime - lastActivityTime;

    console.log("Session check:", {
      lastActivityTime,
      currentTime,
      timeSinceLastActivity,
      warningShown: localStorage.getItem(WARNING_SHOWN_KEY)
    });

    // If more than 15 minutes passed, logout immediately
    if (timeSinceLastActivity >= LOGOUT_TIME) {
      console.log("Session expired, logging out");
      logout();
      return true;
    }
    
    // If between 14-15 minutes passed, show warning immediately
    if (timeSinceLastActivity >= WARNING_TIME) {
      console.log("Showing warning immediately");
      const timeRemaining = LOGOUT_TIME - timeSinceLastActivity;
      const secondsRemaining = Math.max(1, Math.floor(timeRemaining / 1000));
      
      // Show warning immediately
      setShowWarning(true);
      setCountdown(secondsRemaining);
      startCountdown(secondsRemaining * 1000);
      return false;
    }
    
    // Calculate time until warning should appear
    const timeUntilWarning = WARNING_TIME - timeSinceLastActivity;
    console.log(`Time until warning: ${timeUntilWarning}ms`);
    
    return false;
  };

  // Logout
  const logout = () => {
    clearAllTimers();
    removeActivityListeners();
    setShowWarning(false);
    setCountdown(60);

    // Clear all session data
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("role");
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem(WARNING_SHOWN_KEY);
    setAuthorizationHeaderToAllRequests(null);

    setIsLoggedOut(true);
  };

  // Countdown with custom initial time
  const startCountdown = (initialTimeMs = 60000) => {
    const initialSeconds = Math.floor(initialTimeMs / 1000);
    setCountdown(initialSeconds);

    // Mark warning as shown
    localStorage.setItem(WARNING_SHOWN_KEY, "true");

    countdownTimer.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer.current);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Show warning modal
  const showWarningPopup = () => {
    removeActivityListeners();
    setShowWarning(true);
    localStorage.setItem(WARNING_SHOWN_KEY, "true");
    startCountdown();
  };

  // Reset timers
  function resetTimer() {
    clearAllTimers();
    setShowWarning(false);
    localStorage.removeItem(WARNING_SHOWN_KEY);
    updateLastActivityTime();

    warningTimer.current = setTimeout(() => {
      showWarningPopup();
    }, WARNING_TIME);

    inactivityTimer.current = setTimeout(() => {
      logout();
    }, LOGOUT_TIME);
  }

  // Stay logged in
  const stayLoggedIn = () => {
    clearAllTimers();
    setShowWarning(false);
    localStorage.removeItem(WARNING_SHOWN_KEY);
    updateLastActivityTime();
    addActivityListeners();
    resetTimer();
  };

  // Load user + start timers when authenticated
  useEffect(() => {
    const token = get("token");
    const role = get("role");
    const user = JSON.parse(get("user") || "null");

    if (token) addToken(token);
    if (user) addUser(user);
    if (role) saveRole(role);

    setAuthorizationHeaderToAllRequests(token);

    if (token) {
      // Store initial activity time if not exists
      if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
        updateLastActivityTime();
      }
      
      // Check session status on load
      const sessionExpired = checkSessionOnLoad();
      
      if (!sessionExpired) {
        // Start timers based on remaining time
        addActivityListeners();
        
        // Calculate time since last activity
        const lastActivityTime = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || "0");
        const timeSinceLastActivity = Date.now() - lastActivityTime;
        
        if (timeSinceLastActivity < WARNING_TIME) {
          // Warning hasn't triggered yet, set timers normally
          const timeUntilWarning = WARNING_TIME - timeSinceLastActivity;
          const timeUntilLogout = LOGOUT_TIME - timeSinceLastActivity;
          
          warningTimer.current = setTimeout(() => {
            showWarningPopup();
          }, timeUntilWarning);
          
          inactivityTimer.current = setTimeout(() => {
            logout();
          }, timeUntilLogout);
          
          console.log(`Timers set: warning in ${timeUntilWarning}ms, logout in ${timeUntilLogout}ms`);
        }
        // If warning is already showing, checkSessionOnLoad will handle it
      }
    }

    return () => {
      clearAllTimers();
      removeActivityListeners();
    };
  }, []);

  // If token removed => stop modal + timers even if App.js stays mounted
  useEffect(() => {
    const checker = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token) {
        clearAllTimers();
        removeActivityListeners();
        setShowWarning(false);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem(WARNING_SHOWN_KEY);
      }
    }, 10000);

    return () => clearInterval(checker);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<center>Loading...</center>}>

          <Switch>
            <Route path={routes.DOCTOR_DASHBOARD_HOME_LINK} component={DoctorArea} />
            <Route path={routes.HOSPITAL_DASHBOARD_HOME_LINK} component={HospitalArea} />
            <Route path={routes.PHARMACY_DASHBOARD_HOME_LINK} component={PharmacyArea} />
            <Route path={routes.LABO_DASHBOARD_HOME_LINK} component={LaboArea} />
            <Route exact path={routes.PUBLIC_LOGIN_LINK} component={LoginPage} />
            <Route exact path={routes.PUBLIC_EXPORT_LINK} component={ExportPage} />
            <Route exact path={routes.PUBLIC_TERMS_LINK} component={TermsPage} />
            <Route exact path={routes.PUBLIC_PRIVCY_LINK} component={PrivacyPolicyPage} />
            <Route exact path={routes.PUBLIC_SIGNUP_LINK} component={SignUpPage} />
            <Route exact path={routes.PUBLIC_FIRST_LOGIN_LINK} component={FirstLoginPage} />
            <Route exact path={routes.PUBLIC_RESET_LINK} component={ResetPage} />
            <Route exact path={routes.PUBLIC_SUCCESS_LINK} component={SuccessPage} />
            <Route exact path={routes.PUBLIC_HOME_LINK} component={HomePage} />
            <Route exact path={routes.PUBLIC_ABOUT_LINK} component={AboutPage} />
            <Route exact path={routes.PUBLIC_STAFF_LINK} component={StaffPage} />
            <Route component={NotFound} />
          </Switch>

          {isLoggedOut && <Redirect to={routes.PUBLIC_LOGIN_LINK} />}

          {/* Warning Modal */}
          {showWarning && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl w-96 text-center">
                <h2 className="text-xl font-bold mb-4">Expiration de la session</h2>
                <p className="text-gray-700 mb-4">
                  Votre session expirera dans{" "}
                  <span className="font-semibold">{countdown}</span> secondes.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  (Le compteur continue même après fermeture du navigateur)
                </p>
                <div className="flex justify-center gap-4 mt-4">
                  <button
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    onClick={stayLoggedIn}
                  >
                    Restez connecté
                  </button>

                  <button
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                    onClick={logout}
                  >
                    Se déconnecter
                  </button>
                </div>
              </div>
            </div>
          )}

        </Suspense>
      </BrowserRouter>

      <AppNotification />
    </QueryClientProvider>
  );
}